<template>
  <s-modal
    class="sort-group-modal"
    v-model="showModal"
    cancel-confirm-message-code="MC006"
    size="lg"
    :is-loading="loading"
    :title="$t('supervision.groups-exhibition-order')"
    :ok-title="$t('general.confirm')"
    :ok-promise="save"
    :confirm-type="confirmationTypes.YES_NO"
    @show="onShow"
    @cancel="cancel"
  >
    <p class="text-gray">{{ $t('supervision.groups-exhibition-order-tip') }}</p>
    <div class="sort-group-modal__container">
      <div class="sort-group-modal__title">
        {{ $t('supervision.groups-sort-list-title') }}
      </div>

      <div class="sort-group-modal__groups-area">
        <draggable
          v-bind="dragOptions"
          :list="groupsToSort"
        >
          <template v-for="group in groupsToSort">
            <div
              class="sort-group-modal__sort-item"
              :key="group.id"
            >
              <transition-group
                type="transition"
                name="flip-list"
                :key="group.id"
              >
                <b-row
                  justify="between"
                  :key="group.id"
                >
                  <b-col
                    class="sort-group-modal__sort-item-name"
                    :class="{ 'sort-group-modal__sort-item-name--disabled':
                    !(group.variables || []).length }"
                    cols="8"
                    lg="7"
                  >
                    {{ group.name }}
                  </b-col>
                  <b-col
                    cols="4"
                    lg="5"
                    class="text-gray text-right"
                  >
                    {{
                      $t('supervision.exhibited-variables', {
                        amount: (group.variables || [])
                          .filter(({ visibility }) => (visibility)).length,
                      })
                    }}
                  </b-col>
                </b-row>
                >
              </transition-group>
            </div>
          </template>
        </draggable>
      </div>
    </div>
  </s-modal>
</template>

<script>
import draggable from 'vuedraggable';
import confirmationTypes from '@/constants/confirmationTypes';
import eventBus from '@/services/globalEventBus';
import event from '@/domain/events/engineeringModulesHeader';
import Service
  from '@/domain/services/installation/supervision/sensors/generalAndParameters';

export default {
  name: 'SortGroupModal',
  components: {
    draggable,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    groups: {
      type: Array,
      required: true,
    },
    assetModuleId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    groupsToSort: null,
    loading: false,
    confirmationTypes,
  }),
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        ghostClass: 'sort-group-modal__sort-item--ghost',
      };
    },
  },
  watch: {
    groups: {
      handler(newValue) {
        if (this.isDifferent(newValue, this.groupsToSort)
          && newValue.every(group => group.id !== null)) {
          this.groupsToSort = [...newValue];
        }
      },
      deep: true,
    },
  },
  methods: {
    onShow() {
      this.groupsToSort = this.groupsToSort || [...this.groups];
    },
    cancel() {
      this.groupsToSort = null;
      this.showModal = false;
    },
    save() {
      const payload = this.groupsToSort.map(({ id }) => id);
      this.loading = true;

      return Service.sortGroups(this.assetModuleId, payload)
        .then(() => {
          this.$pushAlert({ code: 'MS016' });
          eventBus.emit(event.refresh, { wait: () => { } });
          this.cancel();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isDifferent(groupA, groupB) {
      if (groupA !== null && groupB !== null && groupA.length === groupB.length) {
        return !groupA.every(a => groupB.some(b => b.id === a.id));
      }
      return true;
    },
  },
};
</script>

<style lang="sass" scoped>
.sort-group-modal
  &__container
    border: 1px solid var(--color-gray-medium-light)
    border-radius: 0.25rem

  &__title
    padding: 8px 30px
    background-color: var(--color-border-inner)
    font-weight: 600

  &__groups-area
    padding: 8px 15px
    max-height: 400px
    overflow-y: auto

  &__sort-item
    padding: 8px 15px
    border: 1px solid var(--color-gray-medium-light)
    border-radius: 0.25rem

    &:not(:last-child)
      margin-bottom: 10px

    &--ghost
      border-style: dotted
      opacity: 0.5

  &__sort-item-name
    font-weight: 600
    color: var(--color-primary)
    word-wrap: break-word

    &--disabled
      opacity: .6
</style>
